import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchPublicExtractExams, invalidateAuthorizedExamsCache, fetchBillingConfig, createCheckoutSession, requestExamInvoice, formatExamPrice } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { writeGuestQcmSession } from '../utils/guestSession';
import { ADAPTIVE_EXAM_ENTRY_KEY } from '../constants/adaptiveEntry';

/**
 * Exam catalog (same UI for guest and signed-in users): search + card grid.
 * @param {'guest'|'auth'} mode
 */
export default function PublicExamHomeScreen({ mode = 'guest', onPractice, onNeedLogin }) {
  const { t, i18n } = useTranslation();
  const isAuth = mode === 'auth';
  const { user, saveSelections, getAuthorizedExams, loading: authLoading } = useAuth();

  const [examens, setExamens] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [pickError, setPickError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingExamId, setSavingExamId] = useState(null);
  const [buyingExamId, setBuyingExamId] = useState(null);
  const [invoicingExamId, setInvoicingExamId] = useState(null);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [query, setQuery] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setPickError(null);
    try {
      if (isAuth) {
        const exRes = await getAuthorizedExams();
        if (!exRes.success) {
          setExamens([]);
          setLoadError(exRes.error || t('exam_hub.errors.load_failed'));
          return;
        }
        setExamens(exRes.examens || []);
      } else {
        const data = await fetchPublicExtractExams();
        if (data.success && Array.isArray(data.examens)) {
          setExamens(data.examens);
        } else {
          setExamens([]);
          setLoadError(data?.error || t('public_home.load_error'));
        }
      }
    } catch (e) {
      setExamens([]);
      const apiMsg = e?.response?.data?.error || e?.response?.data?.message;
      const msg = apiMsg || e?.message || (isAuth ? t('exam_hub.errors.network') : t('public_home.load_error'));
      setLoadError(typeof msg === 'string' ? msg : t('public_home.load_error'));
    } finally {
      setLoading(false);
    }
  }, [isAuth, getAuthorizedExams, t]);

  useEffect(() => {
    if (isAuth && authLoading) return;
    refresh();
  }, [refresh, isAuth, authLoading]);

  useEffect(() => {
    let cancelled = false;
    fetchBillingConfig()
      .then((data) => {
        if (!cancelled) setBillingEnabled(!!data?.enabled);
      })
      .catch(() => {
        if (!cancelled) setBillingEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const q = String(query || '').trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return examens;
    return examens.filter((ex) => {
      const name = String(ex.name || '').toLowerCase();
      const id = String(ex.examId || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [examens, q]);

  const handlePickGuest = (ex) => {
    writeGuestQcmSession({
      examId: ex.examId,
      examType: ex.examType || 'qcm',
      strategyId: 'random',
    });
    try {
      localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'practice');
    } catch {
      /* ignore */
    }
    if (onPractice) onPractice();
  };

  const handlePickAuth = async (ex) => {
    setPickError(null);
    setSavingExamId(ex.examId);
    try {
      invalidateAuthorizedExamsCache();
      const strategyForSave = String(user?.lastSelections?.strategyId || '').trim() || 'random';
      const res = await saveSelections({
        examId: ex.examId,
        strategyId: strategyForSave,
      });
      if (!res.success) {
        setPickError(res.error || t('exam_hub.save_failed'));
        return;
      }
      try {
        localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'practice');
      } catch {
        /* ignore */
      }
      if (onPractice) onPractice();
    } finally {
      setSavingExamId(null);
    }
  };

  const handleBuy = async (e, ex) => {
    e.preventDefault();
    e.stopPropagation();
    setPickError(null);
    if (!isAuth) {
      if (onNeedLogin) onNeedLogin();
      else window.dispatchEvent(new Event('request_open_login_modal'));
      return;
    }
    setBuyingExamId(ex.examId);
    try {
      const data = await createCheckoutSession(ex.examId);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setPickError(data?.message || t('billing.checkout_failed'));
    } catch (err) {
      const api = err?.response?.data;
      if (api?.error === 'already_entitled') {
        setPickError(t('billing.already_entitled'));
      } else if (api?.error === 'stripe_not_configured') {
        setPickError(t('billing.not_configured'));
      } else {
        setPickError(api?.message || err.message || t('billing.checkout_failed'));
      }
    } finally {
      setBuyingExamId(null);
    }
  };

  const handleInvoice = async (e, ex) => {
    e.preventDefault();
    e.stopPropagation();
    setPickError(null);
    if (!isAuth) {
      if (onNeedLogin) onNeedLogin();
      else window.dispatchEvent(new Event('request_open_login_modal'));
      return;
    }
    setInvoicingExamId(ex.examId);
    try {
      const data = await requestExamInvoice(ex.examId);
      if (data?.hostedInvoiceUrl) {
        window.open(data.hostedInvoiceUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      setPickError(data?.message || t('billing.invoice_failed'));
    } catch (err) {
      const api = err?.response?.data;
      setPickError(api?.message || err.message || t('billing.invoice_failed'));
    } finally {
      setInvoicingExamId(null);
    }
  };

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: '#e8edf3',
        color: '#0f172a',
      }}
    >
      <header
        style={{
          backgroundColor: '#2c3e50',
          borderBottom: '1px solid #1f2d3a',
          padding: '22px 20px 26px',
        }}
      >
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <h1
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 800,
              letterSpacing: -0.4,
              color: '#f8fafc',
            }}
          >
            {t('public_home.title')}
          </h1>
          <p
            style={{
              margin: '10px 0 0',
              fontSize: 15,
              lineHeight: 1.55,
              color: '#bdc3c7',
              maxWidth: 720,
            }}
          >
            {t('public_home.subtitle')}
          </p>
        </div>
      </header>

      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '22px 14px 40px', boxSizing: 'border-box' }}>
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: 14,
            padding: '22px 22px 26px',
            boxShadow: '0 2px 12px rgba(15,23,42,0.06)',
            border: '1px solid #dce3ec',
          }}
        >
          <label
            htmlFor="exam-search"
            style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#334155' }}
          >
            {t('public_home.search_label')}
          </label>
          <input
            id="exam-search"
            type="search"
            autoComplete="off"
            placeholder={t('public_home.search_placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: '100%',
              maxWidth: 440,
              padding: '14px 16px',
              fontSize: 16,
              borderRadius: 12,
              border: '1px solid #cbd5e1',
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: 8,
            }}
          />

          {pickError && (
            <p style={{ margin: '12px 0 0', color: '#b91c1c', fontSize: 14 }} role="alert">
              {pickError}
            </p>
          )}

          {loading && (
            <p style={{ margin: '18px 0 0', color: '#64748b', fontSize: 15 }}>{t('loading')}</p>
          )}

          {loadError && !loading && (
            <div
              style={{
                marginTop: 18,
                padding: 16,
                borderRadius: 12,
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
              }}
              role="alert"
            >
              <p style={{ margin: '0 0 12px', fontSize: 15 }}>{loadError}</p>
              <button
                type="button"
                onClick={refresh}
                style={{
                  padding: '10px 18px',
                  fontSize: 14,
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: 10,
                  backgroundColor: '#dc2626',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t('public_home.retry')}
              </button>
            </div>
          )}

          {!loading && !loadError && filtered.length === 0 && (
            <p style={{ margin: '18px 0 0', color: '#64748b', fontSize: 15 }}>{t('public_home.empty')}</p>
          )}

          {!loading && filtered.length > 0 && (
            <ul
              style={{
                listStyle: 'none',
                margin: '20px 0 0',
                padding: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(272px, 100%), 1fr))',
                gap: 18,
              }}
            >
              {filtered.map((ex) => {
                const busy = savingExamId === ex.examId;
                const buying = buyingExamId === ex.examId;
                const invoicing = invoicingExamId === ex.examId;
                const showBuy =
                  billingEnabled &&
                  ex.purchasable &&
                  ex.accessSource !== 'assigned';
                const priceLabel = showBuy
                  ? formatExamPrice(ex.priceAmountCents, ex.currency, i18n.language)
                  : '';
                return (
                  <li key={ex.examId}>
                    <div
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '20px 20px 18px',
                        borderRadius: 16,
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#fafafa',
                        boxShadow: '0 4px 14px rgba(15,23,42,0.06)',
                        minHeight: 112,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        boxSizing: 'border-box',
                      }}
                    >
                      <button
                      type="button"
                      disabled={busy}
                      onClick={() => (isAuth ? handlePickAuth(ex) : handlePickGuest(ex))}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: 0,
                        border: 'none',
                        background: 'none',
                        cursor: busy ? 'wait' : 'pointer',
                        opacity: busy ? 0.75 : 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 18,
                          fontWeight: 800,
                          lineHeight: 1.25,
                          color: '#0f172a',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                      >
                        {ex.name || ex.examId}
                      </span>
                      {isAuth && ex.accessSource && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                            color: ex.accessSource === 'assigned' ? '#1e40af' : '#0d9488',
                            alignSelf: 'flex-start',
                            padding: '3px 8px',
                            borderRadius: 6,
                            backgroundColor: ex.accessSource === 'assigned' ? '#dbeafe' : '#ccfbf1',
                          }}
                        >
                          {ex.accessSource === 'assigned'
                            ? t('exam_catalog.tag_assigned')
                            : t('exam_catalog.tag_free_tour')}
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: 12,
                          color: '#64748b',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          whiteSpace: 'normal',
                          wordBreak: 'break-all',
                        }}
                      >
                        {ex.examId}
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          color: '#3498db',
                          fontWeight: 600,
                          marginTop: 'auto',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                      >
                        {busy ? t('exam_catalog.saving') : isAuth ? '→' : `${t('public_home.card_hint')} →`}
                      </span>
                    </button>
                    {showBuy && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                        <button
                          type="button"
                          disabled={buying || invoicing}
                          onClick={(e) => handleBuy(e, ex)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: 13,
                            fontWeight: 700,
                            border: 'none',
                            borderRadius: 10,
                            backgroundColor: '#1d4ed8',
                            color: '#fff',
                            cursor: buying ? 'wait' : 'pointer',
                            opacity: buying ? 0.75 : 1,
                          }}
                        >
                          {buying
                            ? t('billing.redirecting')
                            : t('billing.unlock', { price: priceLabel })}
                        </button>
                        {isAuth && (
                          <button
                            type="button"
                            disabled={buying || invoicing}
                            onClick={(e) => handleInvoice(e, ex)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              fontSize: 12,
                              fontWeight: 600,
                              border: '1px solid #cbd5e1',
                              borderRadius: 10,
                              backgroundColor: '#fff',
                              color: '#334155',
                              cursor: invoicing ? 'wait' : 'pointer',
                            }}
                          >
                            {invoicing ? t('billing.sending_invoice') : t('billing.request_invoice')}
                          </button>
                        )}
                      </div>
                    )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
